import { create } from 'zustand';
import api from '../lib/api';
import { Notification } from '../types';

interface NotificationState {
  notifications: Notification[];
  unreadCount: number;
  isLoading: boolean;
  _authFailed: boolean;
  resetAuthGate: () => void;
  fetchNotifications: () => Promise<void>;
  fetchUnreadCount: () => Promise<void>;
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
}

export const useNotificationStore = create<NotificationState>((set, get) => ({
  notifications: [],
  unreadCount: 0,
  isLoading: false,
  _authFailed: false,

  resetAuthGate: () => set({ _authFailed: false }),

  fetchNotifications: async () => {
    if (get()._authFailed) return;
    try {
      set({ isLoading: true });
      const response = await api.get('/notifications');
      set({ notifications: response.data, isLoading: false });
    } catch (error) {
      if ((error as any)?.response?.status === 401) {
        set({ isLoading: false, _authFailed: true });
        return;
      }
      console.error('Failed to fetch notifications:', error);
      set({ isLoading: false });
    }
  },

  fetchUnreadCount: async () => {
    if (get()._authFailed) return;
    try {
      const response = await api.get('/notifications/unread-count');
      set({ unreadCount: response.data.count, _authFailed: false });
    } catch (error) {
      if ((error as any)?.response?.status === 401) {
        // Expected when logged out or session expired; avoid noisy console errors.
        set({ unreadCount: 0, _authFailed: true });
        return;
      }
      console.error('Failed to fetch unread count:', error);
      set({ unreadCount: 0 });
    }
  },

  markAsRead: async (id: string) => {
    try {
      await api.put(`/notifications/${id}/read`);
      set({
        notifications: get().notifications.map((n) =>
          n.id === id ? { ...n, isRead: true } : n
        ),
        unreadCount: Math.max(0, get().unreadCount - 1),
      });
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  },

  markAllAsRead: async () => {
    try {
      await api.put('/notifications/mark-all-read');
      set({
        notifications: get().notifications.map((n) => ({ ...n, isRead: true })),
        unreadCount: 0,
      });
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  },
}));

